import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync';
import { configureStore, Middleware } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { combineReducers, AnyAction } from 'redux';
import { persistReducer, persistStore, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';

// Import reducers
import loginReducer from './reducers/login';
import userDetailsReducer from './reducers/userDetails';
import sidebarReducer from './reducers/sidebar';
import logoutReducer from './reducers/logout';
import companySkipReducer from './reducers/companySkip';
import fySettingsReducer from './reducers/fySettings';
import companySipSaveReducer from './reducers/companySkipSave';
import gstStatusReducer from './reducers/gstStatus';
import newFyStepsReducer from './reducers/FYSetupStep';
import closeBookFYDetailsReducer from './reducers/closeBookFYDetails';
import openingBalanceSearchReducer from './reducers/openingBalanceSearchSlice';
import userPrefrenceandReducer from './reducers/UserPreference';
import companyReducer from './reducers/CompanySlice';
import PaymentSettingsReducer from './reducers/PaymentSettingsSlice';
import InvoiceSettingsReducer from './reducers/InvoiceSettings';
import DashboardDataReducer from './reducers/DashboardSlice';
import titleSlice from './reducers/titleSlice';
import AccountGroupsMasterReducer from './reducers/AccountGroupSlice';
import BusinessOverviewSlice from './reducers/BusinessOverviewSlice';
import OpeningBalanceSlice from './reducers/OpeningBalanceSlice';
import BizViewSlice from './reducers/BizViewSlice';
import ItemLotDetailsReducer from './reducers/ItemLotDetailsReducer';
import ItemStockSlice from './reducers/ItemStockSlice';
import eInvReducer from './reducers/eInvReducer';
import posList from './reducers/posList';
import countryList from './reducers/countryList';
import stateList from './reducers/stateList';
import initialRenderSlice from './reducers/initialRenderSlice';
import controllNavigationSlice from './reducers/controllNavigation';

// Import the API slice
import { listAccountMasterv2Api } from './apis/accountMaster.api';
import { listAccountBalanceApi } from './apis/listAccountBalance.api';
import { getCodeTranslationApi } from './apis/getCodeTranslation.api';
import { getCompanyCodeTranslationApi } from './apis/getCompanyCodeTranslation.api';
import { trialBalanceApi } from './apis/trailBalance.api';
import { balanceSheetApi } from './apis/balanceSheet.api';
import { profitNLossApi } from './apis/profitNLoss.api';
import { listGroupMasterApi } from './apis/accountGroupMaster.api';
import { listusercompaniesApi } from './apis/companyList.api';
import { getCompanyApi } from './apis/getCompany.api';
import { listUserpreferenceApi } from './apis/listUserPreference.api';
import { listAccountOpeningBalanceApi } from './apis/accountOpeningBalance.api';
import { itemCategory } from './apis/itemCategory.api';
import { InvoiceSettingsApi } from './apis/invoiceSettingsApi.api';
import { paymentsettingsApi } from './apis/paymentSettings.api';
import { VoucherSettingsApi } from './apis/newVoucherSettings.api';
import { priceTypeApi } from './apis/pricetype.api';
import tdsSlice from './reducers/tdsSlice';
import { dashboardApi } from './apis/dashboardApi';
import { CreatePdfApi } from './apis/createPdf.api';
import { emailMasterApi, emailMasterTemplateApi } from './apis/emailMaster.api';
import { gstReportApi } from './apis/GSTReport.api';
import pdfStatusReducer from './reducers/pdfStatusSlice';
import { appVersionApi } from '../features/VersionAlert';
import { companyPreferenceApi } from './apis/companyPreference.api';
import { listFYTransferHistoryApi } from './apis/listFYTransferHistory';
import { stockManagementApi } from '../features/StockManagement/apis/stockManagement.api';
import { listAccountMasterv3Api } from '../features/AccountsAndGroups/api/accountMaster.api';
import { searchHSNApi } from '../features/AccountsAndGroups/api/hsnsac.api';
import { sendEmailApi } from '../features/Email';
import featureFlagReducer from './reducers/featureFlagSlice';
import inventoryItemSelectionReducer from './reducers/inventoryItemSelectionSlice';
import { tcsTdsApi } from '../features/Payment/api/tcsTds.api';
import { paymentApi } from '../features/Payment/api/payment.api';
import { companyCodeTranslationApi } from '../features/CompanycodeTranslation/Api/CompanyCodeTranslation.api';
import { locationApi } from '../features/CompanycodeTranslation/Api/countryApi';
import { districtApi } from '../features/CompanycodeTranslation/Api/countryApi';

import { BuzIndTypeApi } from '../features/CompanySetup/Api/BuzIndType.api';
import { OpeningBalanceDateApi } from './apis/openingbalacedate.api';
import { voucherSettingsApiv2 } from './apis/VoucherSettings.api';
import ifscFetchApi from './apis/ifscFetch.api';
import { setOffApi } from '../features/LedgerTransactions/api/setoff.api';
import pincodeLocationApi from '../features/CompanycodeTranslation/Api/pincodeLocation.api';
import { gstr1Api } from '../features/Gstr1/api/gstr1.api';
import { gstr3bApi } from '../features/Gstr3b/api/gstr3b.api';
import { accountDeletionApi } from '../features/Accounts';

const reducers = combineReducers({
  loginActions: loginReducer,
  eInvReducer,
  posListReducer: posList,
  countryListReducer: countryList,
  tdsSlice,
  stateListReducer: stateList,
  userDetailsReducer,
  sidebarReducer,
  logoutReducer,
  companySkipReducer,
  fySettingsReducer,
  companySipSaveReducer,
  gstStatusReducer,
  newFyStepsReducer,
  openingBalanceSearchReducer,
  closeBookFYStateReducer: closeBookFYDetailsReducer,
  userPrefrenceandReducer, // 👈 keep as-is
  companyReducer,
  paymentSettingsReducer: PaymentSettingsReducer,
  invoiceSettingsReducer: InvoiceSettingsReducer,
  DashboardDataReducer,
  title: titleSlice,
  AccountGroupsMasterReducer,
  BusinessOverviewReducer: BusinessOverviewSlice,
  OpeningBalanceSlice,
  BizViewSlice,
  ItemLotDetailsReducer,
  ItemStockReducer: ItemStockSlice,
  initialRenderSlice,
  controllNavigationSlice,
  [listAccountMasterv2Api.reducerPath]: listAccountMasterv2Api.reducer,
  [listAccountBalanceApi.reducerPath]: listAccountBalanceApi.reducer,
  [getCodeTranslationApi.reducerPath]: getCodeTranslationApi.reducer,
  [getCompanyCodeTranslationApi.reducerPath]: getCompanyCodeTranslationApi.reducer,
  [trialBalanceApi.reducerPath]: trialBalanceApi.reducer,
  [balanceSheetApi.reducerPath]: balanceSheetApi.reducer,
  [profitNLossApi.reducerPath]: profitNLossApi.reducer,
  [listGroupMasterApi.reducerPath]: listGroupMasterApi.reducer,
  [listusercompaniesApi.reducerPath]: listusercompaniesApi.reducer,
  [getCompanyApi.reducerPath]: getCompanyApi.reducer,
  [listUserpreferenceApi.reducerPath]: listUserpreferenceApi.reducer,
  [listAccountOpeningBalanceApi.reducerPath]: listAccountOpeningBalanceApi.reducer,
  [itemCategory.reducerPath]: itemCategory.reducer,
  [InvoiceSettingsApi.reducerPath]: InvoiceSettingsApi.reducer,
  [paymentsettingsApi.reducerPath]: paymentsettingsApi.reducer,
  [VoucherSettingsApi.reducerPath]: VoucherSettingsApi.reducer,
  [priceTypeApi.reducerPath]: priceTypeApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [CreatePdfApi.reducerPath]: CreatePdfApi.reducer,
  [emailMasterTemplateApi.reducerPath]: emailMasterTemplateApi.reducer,
  [emailMasterApi.reducerPath]: emailMasterApi.reducer,
  [gstReportApi.reducerPath]: gstReportApi.reducer,
  pdfStatus: pdfStatusReducer,
  [appVersionApi.reducerPath]: appVersionApi.reducer,
  [companyPreferenceApi.reducerPath]: companyPreferenceApi.reducer,
  [listFYTransferHistoryApi.reducerPath]: listFYTransferHistoryApi.reducer,
  [stockManagementApi.reducerPath]: stockManagementApi.reducer,
  [listAccountMasterv3Api.reducerPath]: listAccountMasterv3Api.reducer,
  [searchHSNApi.reducerPath]: searchHSNApi.reducer,
  [sendEmailApi.reducerPath]: sendEmailApi.reducer,
  featureFlagReducer,
  inventoryItemSelectionReducer,
  [tcsTdsApi.reducerPath]: tcsTdsApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [companyCodeTranslationApi.reducerPath]: companyCodeTranslationApi.reducer,
  [locationApi.reducerPath]: locationApi.reducer,
  [districtApi.reducerPath]: districtApi.reducer,
  [BuzIndTypeApi.reducerPath]: BuzIndTypeApi.reducer,
  [OpeningBalanceDateApi.reducerPath]: OpeningBalanceDateApi.reducer,
  [voucherSettingsApiv2.reducerPath]: voucherSettingsApiv2.reducer,
  [ifscFetchApi.reducerPath]: ifscFetchApi.reducer,
  [setOffApi.reducerPath]: setOffApi.reducer,
  [pincodeLocationApi.reducerPath]: pincodeLocationApi.reducer,
    [gstr1Api.reducerPath]: gstr1Api.reducer,
  [gstr3bApi.reducerPath]: gstr3bApi.reducer,
  [accountDeletionApi.reducerPath]: accountDeletionApi.reducer,
});

// Config for persisting the Redux store
const persistConfig = {
  key: 'root',
  storage,
  whitelist: [
    'loginActions',
    'userDetailsReducer',
    'userPrefrenceandReducer', // 👈 stays
    'featureFlagReducer',
    'companyReducer',
    'controllNavigationSlice',
    'title',
    'DashboardDataReducer',
    'BizViewSlice',
    'logoutReducer',
    'createPdfApi',
    'pdfStatus',
    'companySipSaveReducer',
    'inventoryItemSelectionReducer',
  ],
};

const reduxStateSyncConfig = {
  whitelist: [
    'company/fetchCompany/fulfilled',
    'userPreference/fetch/fulfilled',
    'userDetails/updateUserDetailsOnSignIn',
    'login/logIn',
    'login/logOut',
    'login/logOutx',
    'pdfStatus/setPdfStatus',
    'gstr1Api/invalidateTags',
    'gstr3bApi/invalidateTags',
  ],
};

// Reset API middleware
const resetApiMiddleware: Middleware = (storeAPI) => (next) => (action: AnyAction) => {
  if (action.type === PURGE || action.type === 'resetApi') {
    const { dispatch } = storeAPI;
    // reset all API slices
    [
      listAccountMasterv2Api,
      listAccountBalanceApi,
      getCodeTranslationApi,
      getCompanyCodeTranslationApi,
      trialBalanceApi,
      balanceSheetApi,
      profitNLossApi,
      listGroupMasterApi,
      listusercompaniesApi,
      getCompanyApi,
      listUserpreferenceApi,
      listAccountOpeningBalanceApi,
      itemCategory,
      InvoiceSettingsApi,
      paymentsettingsApi,
      VoucherSettingsApi,
      priceTypeApi,
      dashboardApi,
      CreatePdfApi,
      emailMasterTemplateApi,
      emailMasterApi,
      gstReportApi,
      appVersionApi,
      companyPreferenceApi,
      listFYTransferHistoryApi,
      stockManagementApi,
      listAccountMasterv3Api,
      searchHSNApi,
      BuzIndTypeApi,
      sendEmailApi,
      tcsTdsApi,
      paymentApi,
      companyCodeTranslationApi,
      locationApi,
      districtApi,
      OpeningBalanceDateApi,
      voucherSettingsApiv2,
      ifscFetchApi,
      setOffApi,
      pincodeLocationApi,
      gstr1Api,
      gstr3bApi,
      accountDeletionApi,
    ].forEach((api) => {
      dispatch(api.util.resetApiState());
    });
  }
  return next(action);
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(resetApiMiddleware)
      .concat(createStateSyncMiddleware(reduxStateSyncConfig))
      // RTK query middlewares
      .concat(
        listAccountMasterv2Api.middleware,
        listAccountBalanceApi.middleware,
        getCodeTranslationApi.middleware,
        getCompanyCodeTranslationApi.middleware,
        trialBalanceApi.middleware,
        balanceSheetApi.middleware,
        profitNLossApi.middleware,
        listGroupMasterApi.middleware,
        listusercompaniesApi.middleware,
        getCompanyApi.middleware,
        listUserpreferenceApi.middleware,
        listAccountOpeningBalanceApi.middleware,
        itemCategory.middleware,
        InvoiceSettingsApi.middleware,
        paymentsettingsApi.middleware,
        VoucherSettingsApi.middleware,
        priceTypeApi.middleware,
        dashboardApi.middleware,
        CreatePdfApi.middleware,
        emailMasterTemplateApi.middleware,
        emailMasterApi.middleware,
        gstReportApi.middleware,
        appVersionApi.middleware,
        companyPreferenceApi.middleware,
        listFYTransferHistoryApi.middleware,
        stockManagementApi.middleware,
        listAccountMasterv3Api.middleware,
        searchHSNApi.middleware,
        sendEmailApi.middleware,
        tcsTdsApi.middleware,
        paymentApi.middleware,
        companyCodeTranslationApi.middleware,
        locationApi.middleware,
        districtApi.middleware,
        BuzIndTypeApi.middleware,
        OpeningBalanceDateApi.middleware,
        voucherSettingsApiv2.middleware,
        ifscFetchApi.middleware,
        setOffApi.middleware,
        pincodeLocationApi.middleware,
        gstr1Api.middleware,
        gstr3bApi.middleware,
        accountDeletionApi.middleware,
      ),
});

export const persistor = persistStore(store);
initMessageListener(store);
